import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { get, isEmpty, intersection } from 'lodash';
import { LIST_ITEMS_SIZE } from 'config';
import Grid from 'components/Grid';
import ListItem from 'components/ListItem';
import PLAYER_TYPES from 'constants/player-types';
import { MISSING_SUBSCRIPTION } from 'constants/visibility-types';
import ContinueWatchingIcon from 'components/Icons/continue-watching';
import EmptyDataStub from 'components/EmptyDataStub';
import { TabContentWrapper } from './StyledComponents';
import { navigateToPlayerPage } from 'actions/player';
import { selectIsParentalControlEnabled, selectParentalControlAge } from 'reducers/parental-lock/selectors';
import useModals from 'hooks/useModals';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import isPgPinNeeded from 'utils/is-pg-pin-needed';
import { resolvePlayerNavLink } from 'helpers/urlhelper';

const TabContent = ({ content, type, emptyPlaceholder, loadExtraItems, removeItem }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const isParentalEnabled = useSelector(selectIsParentalControlEnabled);
  const age = useSelector(selectParentalControlAge);

  const ROW_SIZE = 3;

  const { showEnterPinModal } = useModals();

  const onEnter = useCallback(
    (item) => {
      const vodPlayerType = get(item, ['type'], 'VOD');
      const goToPlayer = () =>
        dispatch(
          navigateToPlayerPage({
            item,
            type: PLAYER_TYPES[['channel', 'catchup'].includes(type) ? 'CHANNEL' : vodPlayerType],
          }),
        );

      if (item.visibilityDetails === MISSING_SUBSCRIPTION) {
        const isSinglePurchase = !isEmpty(intersection(item.types, ['EST', 'TVOD']));
        const channelData = type === 'channel' ? `content_type=CHANNEL; channel_id=${item.id}` : '';
        const vodData =
          type === 'vod' ? `vod_asset_id=${item.id}; content_type=${get(item, 'metadata.contentType', item.type)}` : '';
        const catchupData =
          type === 'catchup'
            ? `vod_asset_id=${get(item, ['epgEvent', 'id'], item.id)}; content_type=${get(
                item,
                ['epgEvent', 'state'],
                item.type,
              )}; channel_id=${get(item, ['epgEvent', 'channel', 'id'], item.id)}`
            : '';
        const addInfo = vodData || channelData || catchupData;

        innerAnalyticsVolia.postEvent({
          eventGroup: 'notification',
          eventName: 'unavailable content',
          eventValue: 'show notification',
          addInfo,
        });

        const onSuccess = resolvePlayerNavLink(item, {
          type: PLAYER_TYPES[['channel', 'catchup'].includes(type) ? 'CHANNEL' : vodPlayerType],
        });

        if (isSinglePurchase) {
          history.push(`/details/VOD_ASSET/${item.id}/tariffs`);
        } else {
          history.push({
            pathname: '/profile/packages/filter',
            search: `?${['channel', 'catchup'].includes(type) ? 'channelId' : 'vodAssetId'}=${
              item.id
            }&onSuccess=${onSuccess}`,
          });
        }
        return;
      }

      if (isParentalEnabled) {
        const isAdultChannel = get(item, ['pgRating', 'minRecomendedAge'], 0) === 18;
        const isPinNeeded = isPgPinNeeded(item.pgRating, age);

        if (isPinNeeded || isAdultChannel) {
          innerAnalyticsVolia.postEvent({
            eventGroup: 'notification',
            eventName: 'PIN request',
            eventValue: 'show notification',
          });

          showEnterPinModal({ sectionToFocus: `@GRID` }, goToPlayer);
          return;
        }
      }
      goToPlayer();
    },
    [isParentalEnabled, dispatch, type, history, age, showEnterPinModal],
  );

  const onFocus = useCallback(
    (_item, index) => {
      const rows = content.data.length / ROW_SIZE;
      const currentRowIndex = Math.ceil(index / ROW_SIZE);
      const isRequiresExtraLoad = currentRowIndex >= rows - 1 - 1;
      if (isRequiresExtraLoad) loadExtraItems(type);
    },
    [content, loadExtraItems, type],
  );

  const dashboardName = type === 'vod' ? 'CONTINUE_WATCHING' : 'RECENT_CHANNELS';

  const onEdit = useCallback((item) => removeItem({ type, item, dashboardName }), [dashboardName, removeItem, type]);

  return (
    <TabContentWrapper>
      {!content.isLoading && (
        <Grid
          items={content.data}
          rowSize={ROW_SIZE}
          showProgress={type !== 'channel'}
          isEditable={true}
          gridId={`grid-continue-watching-${type}`}
          key={`grid-continue-watching-${type}`}
          onEditItem={onEdit}
          onEnter={onEnter}
          onFocus={onFocus}
          component={ListItem}
          itemSize={LIST_ITEMS_SIZE['LARGE']}
          dashboardName={dashboardName}
          withoutContent={<EmptyDataStub emptyPlaceholder={emptyPlaceholder} icon={<ContinueWatchingIcon />} />}
        />
      )}
    </TabContentWrapper>
  );
};

TabContent.defaultProps = {
  content: {},
};

TabContent.propTypes = {
  content: PropTypes.shape({
    isLoading: PropTypes.bool.isRequired,
    data: PropTypes.array.isRequired,
  }).isRequired,
  type: PropTypes.string.isRequired,
  emptyPlaceholder: PropTypes.string.isRequired,
  loadExtraItems: PropTypes.func.isRequired,
  removeItem: PropTypes.func.isRequired,
};

export default TabContent;
